import { useQuery } from '@tanstack/react-query';
import { useUserStore } from '@/store';
import { getUserRoles, getUserPermissions } from './service';
import { hasPermission, hasAnyPermission, hasAllPermissions, hasRole, hasAnyRole, getHighestRole } from './utils';
import type { PermissionCode, RoleSlug, UserRole } from './types';
import { PERMISSION_CODES, ROLE_SLUGS } from './types';

const ADMIN_ROLES: RoleSlug[] = [ROLE_SLUGS.ADMIN, ROLE_SLUGS.SUPER_ADMIN];

export function usePermissions() {
  const user = useUserStore((s) => s.user);

  const rolesQuery = useQuery({
    queryKey: ['user-roles', user?.id],
    queryFn: () => getUserRoles(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  const permissionsQuery = useQuery({
    queryKey: ['user-permissions', user?.id],
    queryFn: () => getUserPermissions(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  const roles = rolesQuery.data ?? [];
  const permissions = permissionsQuery.data ?? [];
  const isLoading = rolesQuery.isLoading || permissionsQuery.isLoading;
  const isAdmin = roles.some((ur) => ADMIN_ROLES.includes(ur.role?.slug as RoleSlug));
  const canCreateKnowledge = hasPermission(permissions, PERMISSION_CODES.KNOWLEDGE_CREATE);
  const canAccessWorkspace =
    isAdmin ||
    hasPermission(permissions, PERMISSION_CODES.ADMIN_ACCESS) ||
    canCreateKnowledge;

  const checkPermission = (code: PermissionCode): boolean => {
    if (hasPermission(permissions, code)) return true;
    if (isAdmin && code === PERMISSION_CODES.ADMIN_ACCESS) return true;
    return false;
  };

  return {
    roles,
    permissions,
    isLoading,
    isAdmin,
    canCreateKnowledge,
    canAccessWorkspace,
    checkPermission,
    checkAnyPermission: (codes: PermissionCode[]) => hasAnyPermission(permissions, codes) || (isAdmin && codes.includes(PERMISSION_CODES.ADMIN_ACCESS)),
    checkAllPermissions: (codes: PermissionCode[]) => hasAllPermissions(permissions, codes),
    checkRole: (slug: RoleSlug) => hasRole(roles, slug),
    checkAnyRole: (slugs: RoleSlug[]) => hasAnyRole(roles, slugs),
    highestRole: getHighestRole(roles),
  };
}

export function useRequirePermission(code: PermissionCode) {
  const { checkPermission, isLoading } = usePermissions();
  return { allowed: checkPermission(code), isLoading };
}

export function useRequireRole(slug: RoleSlug) {
  const { checkRole, isLoading } = usePermissions();
  return { allowed: checkRole(slug), isLoading };
}

export type { PermissionCode, RoleSlug, UserRole };
